/**
 * The active-workspace pick, persisted across two browser stores under one key name
 * (steward-app#1401). Split out of `WorkspaceProvider.tsx` into its own module so the
 * sign-out path (`auth/useAuth.ts`) can clear it WITHOUT creating an import cycle:
 * `WorkspaceProvider.tsx` already imports `useAccessToken` from `auth/useAuth.ts`, so
 * `useAuth.ts` importing anything back from `WorkspaceProvider.tsx` would close a loop.
 * Neither module here imports the other side of that loop.
 *
 * Which store wins on read is the whole mechanism:
 *
 *   - `sessionStorage` holds THIS TAB's own current workspace. It is what makes a
 *     tab-only pick (an advisor's "Open" on a client card, `AdvisorClients.tsx`)
 *     invisible to every other tab and to the next sign-in — a fresh tab, and a fresh
 *     session, both start with an empty sessionStorage.
 *   - `localStorage` holds the durable, cross-tab "home" — for an advisor, their own
 *     firm workspace; for anyone else, whichever workspace an explicit pick last named.
 *
 * `readStored()` prefers sessionStorage: if THIS tab has its own current workspace,
 * that is what a reload in this tab should resolve to, regardless of what any other
 * tab (or a previous session) left in the shared store. Falling through to
 * localStorage is what makes a brand-new tab, and the next sign-in, land in the
 * durable home instead — sessionStorage there is empty by construction (a new tab
 * gets a new session-storage area; sign-out clears THIS tab's session value too, see
 * {@link clearStoredWorkspaceId}, so a same-tab sign-out/sign-in falls through the
 * same way a brand-new tab would).
 *
 * Only an explicit choice is written (steward-app#2623): `switchWorkspace`, create,
 * accept-invite, delete-active and `persistActiveWorkspaceId`. A fallback that
 * `resolveActive` picks because the stored id is not usable applies for the session and is
 * never written, so it cannot replace the user's own remembered workspace.
 *
 * `writeSessionStored`/`writeLocalStored` are kept separate (rather than one function
 * that always writes both) because {@link writeStored}'s `remember: 'tab'` mode needs to
 * write ONE store and leave the other alone. Writing both unconditionally for a tab-only
 * pick is the bug steward-app#1401 fixed: it leaked a tab-only client pick into the shared
 * key and let a second tab inherit it at its own next reload.
 */
const STORAGE_KEY = 'steward-app:workspaceId';

/**
 * The shape the façade accepts for a PRESENT `X-Workspace-Id`: 26 characters of `[0-9A-Z]`
 * (`WORKSPACE_HEADER_SHAPE` in `services/steward-app-api/src/dispatch.ts`, steward-app#325).
 * Mirrors that gate on purpose, and is the LOOSE class rather than strict Crockford for the
 * same reason: dev ids such as `01HZDEVSTWRDPAUSWS00000001` contain `U`, so a stricter check
 * here would discard a real workspace's stored pick.
 *
 * Why it is applied on READ. The stored value is the active id until `/me` resolves, and it
 * is sent on `GET /me` itself. A stored value the façade refuses would 400 `/me`, stay the
 * active id, so the SPA could never heal (before #2623 it was also written straight back
 * by the provider's store-sync effect). Before the gate `/me` ignored the header and the SPA recovered. A value that fails
 * is treated as if nothing were stored. This is not authorization: a well-formed id the user
 * no longer belongs to is still settled by `resolveActive` against `/me`.
 */
const STORED_ID_SHAPE = /^[0-9A-Z]{26}$/;
const wellFormed = (v: string | null): string | null => (v !== null && STORED_ID_SHAPE.test(v) ? v : null);

export function readSessionStored(): string | null {
  try { return wellFormed(sessionStorage.getItem(STORAGE_KEY)); } catch { return null; }
}
export function readLocalStored(): string | null {
  try { return wellFormed(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}
export function readStored(): string | null {
  const session = readSessionStored();
  return session !== null ? session : readLocalStored();
}
export function writeSessionStored(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore storage failures (private browsing) */ }
}
export function writeLocalStored(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore storage failures (private browsing) */ }
}
/** Writes sessionStorage always, and localStorage too UNLESS `remember: 'tab'` — the
 *  advisor-portal "Open" case, where the pick must stay invisible outside this tab. See
 *  this module's header above for the read side of the same decision. */
export function writeStored(id: string | null, remember: 'both' | 'tab' = 'both'): void {
  writeSessionStored(id);
  if (remember === 'both') writeLocalStored(id);
}

/**
 * Clear the remembered active workspace from the TAB store only — the sign-out path
 * (`useAuth.ts`'s `signOut`), which never runs inside a mounted `WorkspaceProvider` and
 * so cannot call `switchWorkspace` (steward-app#1401).
 *
 * Without this, a same-tab sign-out followed by a sign-IN as the SAME user can
 * resurrect whichever client an advisor had open: `sessionStorage` survives the round
 * trip through Cognito's hosted UI (same browsing context — no tab close, no new
 * session-storage area), and the client workspace is a genuine, still-active
 * membership for that advisor, so `resolveActive` (`WorkspaceProvider.tsx`) would
 * accept it right back on the next `/me`. Clearing it here closes that without the
 * auth layer needing to know anything about workspaces beyond this one call.
 *
 * ⚠️ **`localStorage` is deliberately left alone** (correction, this branch's own code
 * review). An earlier version of this function cleared both, on the reasoning that
 * sign-out should leave no trace of "a stale client pick" anywhere. That over-reached:
 * `localStorage` only ever holds a client workspace when someone made it their durable
 * home through an explicit, non-tab-only pick (the plain workspace switcher, or one of
 * `useClientFrame`'s own exits) — a deliberate choice that predates #1401 and that this
 * issue never asked to take away, not the "opened a client, never chose it as home"
 * case #1401 is about. Wiping it on every sign-out would have thrown away that durable
 * "last workspace" memory for every user, advisor or not, on every sign-out — a real
 * regression for no gain, since `resolveActive` re-validates whatever `localStorage`
 * holds against the SIGNED-IN user's own `/me` memberships regardless of who last wrote
 * it, so a stale or another user's id there was never a way to LAND in a workspace this
 * user cannot open. Only the tab-only value needed to stop surviving the round trip.
 *
 * ⚠️ **A narrower, PRE-EXISTING truth this correction does not extend to** (independent
 * review, verified against `WorkspaceProvider.tsx`): `setActiveWorkspaceId` runs in the
 * render body, ahead of `resolveActive` settling against a fresh `/me`, so on the FIRST
 * render after a sign-in the previously stored id (whichever store it came from) is
 * briefly the one `activeWorkspaceId` reports, and any request that fires off that value
 * before `/me` resolves does carry it — appcore then 403s those, since the new caller
 * is not yet known to be a member. That is unchanged from `main` and predates this
 * function entirely; fixing it is out of scope here and is filed separately.
 */
export function clearStoredWorkspaceId(): void {
  writeSessionStored(null);
}
